import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import Tooltip from "../../tooltip/Tooltip";
import "./_table-actions-menu.scss";

const INLINE_ACTIONS_LIMIT = 2;

/**
 * Row-actions for a table's "Actions" column: the first two items always
 * render as their own inline buttons, matching the design's row of "View" /
 * "Log activity"-style buttons. Any items beyond that collapse behind a
 * three-dot menu instead of clipping/overlapping - the two most-used
 * actions stay one click away regardless of how many total actions a row has.
 *
 * `items`: [{ title, action, disabled?, tooltip? }]
 */
const TableActionsMenu = ({ items }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const close = () => setIsOpen(false);

  const handleToggle = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    }
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = (item, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (item?.disabled) return;
    try {
      item.action?.();
    } catch (error) {
      console.error("Table action failed:", error);
    }
    close();
  };

  // Manual outside-click handling, matching the shared portal pattern used
  // elsewhere (ColumnSelect, IntegrationCardMenu) - the dropdown is portaled
  // outside this component's own DOM subtree.
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        dropdownRef.current &&
        !triggerRef.current.contains(event.target) &&
        !dropdownRef.current.contains(event.target)
      ) {
        close();
      }
    };
    document.addEventListener("mousedown", handleClickOutside, true);
    return () => document.removeEventListener("mousedown", handleClickOutside, true);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    setPosition((prev) => ({
      top: rect.bottom > window.innerHeight ? window.innerHeight - rect.height - 10 : prev.top,
      left: rect.right > window.innerWidth ? window.innerWidth - rect.width - 10 : prev.left,
    }));
  }, [isOpen]);

  const portalRoot = typeof document !== "undefined" && document.getElementById("table-action-root");

  if (!items?.length) return null;

  const renderItem = (item, index) => {
    const button = (
      <button
        key={item.title ?? index}
        type="button"
        className="ui-table-actions__item"
        onClick={(e) => handleItemClick(item, e)}
        aria-disabled={item?.disabled || undefined}
      >
        {item.title}
      </button>
    );
    return item?.disabled && item?.tooltip ? (
      <Tooltip key={item.title ?? index} content={item.tooltip}>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  if (items.length <= INLINE_ACTIONS_LIMIT) {
    return (
      <div
        className="ui-table-actions"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {items.map(renderItem)}
      </div>
    );
  }

  // More than the limit: keep the first two as their own inline buttons
  // (the row's most-used actions) and collapse only the rest behind the
  // three-dot trigger, instead of hiding every action once a row crosses
  // the limit.
  const inlineItems = items.slice(0, INLINE_ACTIONS_LIMIT);
  const overflowItems = items.slice(INLINE_ACTIONS_LIMIT);

  return (
    <div
      className="ui-table-actions"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {inlineItems.map(renderItem)}

      <div
        ref={triggerRef}
        className="ui-table-actions__trigger"
        onClick={handleToggle}
        data-testid="table-actions-menu-trigger"
      >
        <MoreVertical size={16} />
      </div>

      {isOpen &&
        portalRoot &&
        createPortal(
          <div
            ref={dropdownRef}
            className="ui-table-actions__dropdown"
            style={{ position: "fixed", top: position.top, left: position.left }}
          >
            {overflowItems.map((item, index) => {
              const entry = (
                <p
                  key={item.title ?? index}
                  className="ui-table-actions__dropdown-item"
                  onClick={(e) => handleItemClick(item, e)}
                  aria-disabled={item?.disabled || undefined}
                >
                  {item.title}
                </p>
              );
              return item?.disabled && item?.tooltip ? (
                <Tooltip key={item.title ?? index} content={item.tooltip}>
                  {entry}
                </Tooltip>
              ) : (
                entry
              );
            })}
          </div>,
          portalRoot
        )}
    </div>
  );
};

export default TableActionsMenu;
